import {
  CONVENTIONS,
  type ConventionId,
  type SourceAxis,
} from "@/shared/state/visualization/schema/v1/three-d";

/**
 * How a preset reads in an axis preset picker, which every 3D entity section
 * shares (`AxisPresetPicker`): a name, the units its axes are read in, and a
 * hint at where data in it comes from.
 */
export interface AxisPresetName {
  title: string;
  /**
   * Each axis's unit, in the order the title names the axes, signed where the
   * axis lands flipped in the scene ("m/-m/m"). Empty where the axes have no
   * one unit to show, as a vector's or an angle's do not.
   */
  subtitle: string;
  /** A hint at what systems or file formats commonly use this preset. */
  formatHint: string;
}

const FORMAT_HINTS: Readonly<Record<ConventionId, string>> = {
  flu: "Common in ROS",
  enu: "Common in ROS",
  ned: "Common in PX4",
  frd: "Common in PX4",
  rdf: "Common in ROS cameras",
  scene: "Left-handed coordinates",
};

const SOURCE_AXES: readonly SourceAxis[] = ["x", "y", "z"];

/**
 * A convention's name in every picker that offers it: its source axes in x/y/z
 * order, and where it is common. One place, so a convention reads the same in
 * the Position, Rotation, Vector Field and Point Cloud sections.
 */
export function conventionName(id: ConventionId): AxisPresetName {
  const { axisNames } = CONVENTIONS[id];
  return {
    title: SOURCE_AXES.map((axis) => axisNames[axis]).join("/"),
    subtitle: "",
    formatHint: FORMAT_HINTS[id],
  };
}

/**
 * A convention's name for coordinates in metres, as a position or a point
 * cloud is read: the units line is each source axis in metres, negative where
 * the convention flips that axis into the scene.
 */
export function metreConventionName(id: ConventionId): AxisPresetName {
  const convention = CONVENTIONS[id];
  const slots = [convention.right, convention.up, convention.forward];
  const unitOf = (axis: SourceAxis) =>
    slots.find((slot) => slot.field === axis)?.scale === -1 ? "-m" : "m";
  return {
    ...conventionName(id),
    subtitle: SOURCE_AXES.map(unitOf).join("/"),
  };
}

/** What a picker shows in its field for a preset: its name, then its units. */
export function axisPresetText(name: AxisPresetName): string {
  return [name.title, name.subtitle].filter(Boolean).join(" ");
}
